// @ts-nocheck
import { Analytics } from '@cobliteam/analytics';
import type { Incident } from '@cobliteam/api-lib';
import { ConditionCategory } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/conditions';
import { RuleCategory } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/rules';
import { type DateRange, type Geofence, Location, type TimestampInMillis, type UUID } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import { Button, Typography } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import moment from 'moment';
import queryString from 'query-string';
import React from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Rectangle, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRightCb } from '../../../../icons';
import type { ProcessedStopRouteStep } from '../../../../store';
import { useScrollRef } from '../../../../useScrollRef';
import { ONE_HOUR_IN_MILLIS } from '../../..';
import { IncidentPopup } from '../../../IncidentPopup/IncidentPopup';
import { StartEndPopup } from '../../../StartEndPopup/StartEndPopup';
import { HistoryStopPopup } from '../HistoryStopPopup/HistoryStopPopup';
import * as styles from './styles';

interface PositionedInfo<T> {
  info: T;
  x: number;
  y: number;
}

interface StartEndInfo {
  timestamp: TimestampInMillis;
  type: 'start' | 'end';
  vehicleId: UUID;
  geofences: Geofence[];
  latitude?: number;
  longitude?: number;
  idleWithEngineOnInMillis: number;
  idleTimeHighlighted?: boolean;
  driverName?: string;
}

export interface StopBarInfo {
  period: [TimestampInMillis, TimestampInMillis];
  vehicleId: UUID;
  isIdle: boolean;
  stop: ProcessedStopRouteStep;
}
export interface IncidentBarInfo {
  period: [TimestampInMillis, TimestampInMillis];
  position: [TimestampInMillis, TimestampInMillis];
  vehicleId: UUID;
  incident: Incident;
  driverName?: string;
}

export interface IntervalInfo {
  period: [TimestampInMillis, TimestampInMillis];
}

// Data key must start with "stop" and be followed by as number (e.g. stop1)
export interface DataItem {
  vehicleId: UUID;
  vehicleName: string;
  routeInterval?: IntervalInfo;
  routeStart?: StartEndInfo;
  routeEnd?: StartEndInfo;
  stops?: { [key: string]: StopBarInfo | undefined };
  incidents?: { [key: string]: IncidentBarInfo | undefined };
}

export interface RoutesTimelineProps {
  period: DateRange;
  wantedTimeSelection: DateRange;
  chartData: DataItem[];
  onRequestAddress: (latitude: number, longitude: number) => void;
}

export const RoutesTimeline = ({ period, wantedTimeSelection, chartData: inputChartData, onRequestAddress }: RoutesTimelineProps) => {
  const { t } = useTranslation('tracking');
  const HEADER_SIZE = 39.8;
  const BAR_ROW_SIZE = 69;
  const BAR_SIZE = 26;

  const TOOLTIP_WIDTH = 410;
  const TOOLTIP_EXT_HEIGHT = 345;
  const MIN_CHART_WIDTH = 920;

  const X_TICK_WIDTH = 24;
  const X_TICK_GAP_MARGIN = 50;
  const X_TICK_END_MARGIN = 35;

  const STOP_TOOLTIP_HEIGHT_OFFSET = 350;
  const START_END_TOOLTIP_HEIGHT_OFFSET = 330;
  const INCIDENT_TOOLTIP_HEIGHT_OFFSET = 310;

  const DEBOUNCE_TOOLTIP = 300; // 0.3s
  const DEBOUNCE_X_AXIS = 50; // 50ms

  const X_AXIS_OFFSET = 230;
  const numExtraRows = inputChartData.length >= 25 ? 0 : 5;

  let timeoutIntervalBar: null | NodeJS.Timer = null;
  let scrollTimeout: null | NodeJS.Timer = null;

  const [tooltipIsHovered, setTooltipIsHovered] = React.useState(false);
  const [tooltipAnchorIsHovered, setTooltipAnchorIsHovered] = React.useState(false);
  const [tooltipTransitionGuard, setTooltipTransitionGuard] = React.useState(false);
  const [scrollOffset, setScrollOffset] = React.useState<number>(0);

  const [focusStartEndInfo, setFocusStartEndInfo] = React.useState<PositionedInfo<StartEndInfo> | undefined>(undefined);
  const [focusStopBarInfo, setFocusStopBarInfo] = React.useState<PositionedInfo<StopBarInfo> | undefined>(undefined);
  const [focusIntervalInfo, setFocusIntervalInfo] = React.useState<PositionedInfo<IntervalInfo> | undefined>(undefined);
  const [focusIncidentInfo, setFocusIncidentInfo] = React.useState<PositionedInfo<IncidentBarInfo> | undefined>(undefined);
  const { scrollableDivRef: scrollRef } = useScrollRef();

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.onscroll = (ev: any) => {
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }

        scrollTimeout = setTimeout(() => {
          const scrollValue: number = ev.target.scrollTop;
          if (scrollValue > X_AXIS_OFFSET) {
            setScrollOffset(scrollValue - X_AXIS_OFFSET);
          } else {
            setScrollOffset(0);
          }
        }, DEBOUNCE_X_AXIS);
      };
    }
  }, [scrollRef.current]);

  React.useEffect(() => {
    if (focusStopBarInfo) {
      onRequestAddress(focusStopBarInfo.info.stop.latitude, focusStopBarInfo.info.stop.longitude);
    }
    if (focusStartEndInfo?.info.latitude && focusStartEndInfo.info.longitude) {
      onRequestAddress(focusStartEndInfo.info.latitude, focusStartEndInfo.info.longitude);
    }
  }, [focusStopBarInfo, focusStartEndInfo]);

  const formatDuration = (valueInMillis: number): string => {
    const duration = moment.duration(valueInMillis);
    const hours = duration.days() * 24 + duration.hours();
    const hoursStr = hours ? `${hours}h` : '';
    const minutesStr = duration.minutes() ? `${duration.minutes()}min` : '';
    return `${hoursStr}${minutesStr}`;
  };

  const isTimestampVisible = (value: TimestampInMillis) => {
    const periodStartBeingSeen = period.start + wantedTimeSelection.start;
    const periodEndBeingSeen = period.start + wantedTimeSelection.end;
    return value >= periodStartBeingSeen && value <= periodEndBeingSeen;
  };

  const CustomStopBar = (stopBarInfo: StopBarInfo | undefined, barProps: any) => {
    if (stopBarInfo) {
      const { x, y, width, height, ...otherBarProps } = barProps;
      const active = Boolean(focusStopBarInfo && focusStopBarInfo.info === stopBarInfo);

      return (
        <Rectangle
          {...otherBarProps}
          x={active ? x - 0.5 : x}
          y={active ? y - 0.5 : y}
          width={active ? width + 1 : width}
          height={active ? height + 1 : height}
          radius={3}
          className={styles.stop(active)}
        />
      );
    }
  };

  const CustomIncidentBar = (incidentBarInfo: any | undefined, barProps: any) => {
    if (incidentBarInfo) {
      const { x, y, ...otherBarProps } = barProps;

      return <Rectangle {...otherBarProps} x={x} y={y} width={5} height={26} radius={3} className={styles.incident(incidentBarInfo.incident.type === 'device_plugged')} />;
    }
  };

  const CustomLabel = (labelProps: any) => {
    const { x, y, value, index, width, height } = labelProps;

    const { vehicleName, routeStart, routeEnd } = chartData[index];
    const label = `${vehicleName} ${t('no_routes_today')}`;

    const ctx = document.createElement('canvas').getContext('2d')!;
    ctx.font = '12px "Geist"';
    const labelWidth = ctx.measureText(label).width;

    const handleMouseEnter = (startEndInfo: StartEndInfo, infoX: number, infoY: number) => () => {
      Analytics.track('Start/End Tooltip On Tracking Timeline Opened');

      const { adjustedX, adjustedY } = adjustPosition({
        x: infoX,
        y: infoY,
        contentHeight: START_END_TOOLTIP_HEIGHT_OFFSET,
      });

      setFocusStopBarInfo(undefined);
      setFocusIncidentInfo(undefined);
      setTooltipAnchorIsHovered(true);
      setFocusStartEndInfo({
        info: startEndInfo,
        x: adjustedX,
        y: adjustedY,
      });
    };

    return value ? (
      <g className={styles.routeLabel}>
        {routeStart && isTimestampVisible(routeStart.timestamp) && (
          <g onMouseEnter={handleMouseEnter(routeStart, x - 2, y + BAR_SIZE)} onMouseLeave={handleTooltipAnchorMouseLeave(setFocusStartEndInfo)}>
            <line id='startEndBar' x1={x - 2} y1={y - 15} x2={x - 2} y2={y + BAR_SIZE} />
            <ArrowRightCb size='xs' x={x + 0.82} y={y - 15.64} className='text-black' />
          </g>
        )}
        {routeEnd && isTimestampVisible(routeEnd.timestamp) && (
          <g onMouseEnter={handleMouseEnter(routeEnd, x + width + 2, y + BAR_SIZE + 15)} onMouseLeave={handleTooltipAnchorMouseLeave(setFocusStartEndInfo)}>
            <line id='startEndBar' x1={x + width + 2} y1={y} x2={x + width + 2} y2={y + BAR_SIZE + 15} />
            <line id='startEndBar' x1={x + width - 3} y1={y + BAR_SIZE + 8} x2={x + width - 3} y2={y + BAR_SIZE + 15} />
          </g>
        )}
      </g>
    ) : (
      <g className={styles.noRouteLabel}>
        <rect x='0' y={y - (BAR_ROW_SIZE - BAR_SIZE) / 2} width='100%' />
        <line id='middleLine' x1='0' y1={y + BAR_SIZE / 2} x2='100%' y2={y + BAR_SIZE / 2} />
        <rect x={chartWidth / 2 - labelWidth / 2 - 20} y={y + 2.5} width={labelWidth + 40} rx='3' />
        <text x={chartWidth / 2 - labelWidth / 2} y={y + 4} dy={height / 2}>
          {label}
        </text>
      </g>
    );
  };

  const getHintInfoNotFilteredDurationInMillis = (refPeriod: [number, number]) => {
    const periodStartBeingSeen = period.start + wantedTimeSelection.start;
    const periodEndBeingSeen = period.start + wantedTimeSelection.end;

    const isStartFiltered = refPeriod[0] < periodStartBeingSeen && refPeriod[1] > periodStartBeingSeen;
    const isEndFiltered = refPeriod[1] > periodEndBeingSeen && refPeriod[0] < periodEndBeingSeen;

    const adjustedStart = Math.max(refPeriod[0], periodStartBeingSeen);
    const adjustedEnd = Math.min(refPeriod[1], periodEndBeingSeen);

    return isStartFiltered || isEndFiltered ? adjustedEnd - adjustedStart : undefined;
  };

  const handleTooltipMouseLeave = () => {
    setTooltipIsHovered(false);
    setTooltipTransitionGuard(true);
    setTimeout(() => setTooltipTransitionGuard(false), 150);
  };

  const getExtraChartRows = (periodStart: number): DataItem[] => {
    // Since this chart is not responsive, this adds needed space for the popups
    return Array(numExtraRows)
      .fill(undefined)
      .map((_, idx) => ({
        vehicleId: `${idx}`,
        vehicleName: '',
        routeInterval: {
          period: [periodStart, periodStart],
        },
      }));
  };

  const handleIntervalMouseEnter = (barProps: any) => {
    if (timeoutIntervalBar) {
      clearTimeout(timeoutIntervalBar);
    }

    timeoutIntervalBar = setTimeout(() => {
      const { x, y, payload } = barProps;

      const info = payload.routeInterval;
      const notFilteredDuration = getHintInfoNotFilteredDurationInMillis(info.period);

      if (notFilteredDuration) {
        const adjustedStartX = x < 0 ? X_TICK_END_MARGIN : x;
        setFocusIntervalInfo({ info, x: adjustedStartX, y });
      }
    }, DEBOUNCE_TOOLTIP);
  };

  const handleIntervalMouseLeave = () => {
    if (timeoutIntervalBar) {
      clearTimeout(timeoutIntervalBar);
      return;
    }
    setFocusIntervalInfo(undefined);
  };

  const handleStopBarMouseEnter = (index: number) => (barProps: any) => {
    if (timeoutIntervalBar) {
      clearTimeout(timeoutIntervalBar);
    }
    timeoutIntervalBar = setTimeout(() => {
      Analytics.track('Stop Tooltip On Tracking Timeline Opened');

      const { adjustedX, adjustedY } = adjustPosition({
        ...barProps,
        contentHeight: STOP_TOOLTIP_HEIGHT_OFFSET,
      });

      setTooltipAnchorIsHovered(true);
      setFocusStopBarInfo({
        info: barProps.payload[`stop${index + 1}`],
        x: adjustedX,
        y: adjustedY,
      });
      setFocusStartEndInfo(undefined);
      setFocusIncidentInfo(undefined);
    }, DEBOUNCE_TOOLTIP);
  };

  const handleIncidentBarMouseEnter = (index: number) => (barProps: any) => {
    if (timeoutIntervalBar) {
      clearTimeout(timeoutIntervalBar);
    }
    timeoutIntervalBar = setTimeout(() => {
      Analytics.track('Incident Tooltip On Tracking Timeline Opened');

      const { adjustedX, adjustedY } = adjustPosition({
        ...barProps,
        contentHeight: INCIDENT_TOOLTIP_HEIGHT_OFFSET,
      });

      setTooltipAnchorIsHovered(true);
      setFocusIncidentInfo({
        info: barProps.payload[`incident${index + 1}`],
        x: adjustedX,
        y: adjustedY,
      });
      setFocusStartEndInfo(undefined);
      setFocusStopBarInfo(undefined);
    }, DEBOUNCE_TOOLTIP);
  };

  const handleTooltipAnchorMouseLeave = (updateAnchor: (value: React.SetStateAction<any>) => void) => () => {
    if (timeoutIntervalBar) {
      clearTimeout(timeoutIntervalBar);
      return;
    }

    setTooltipTransitionGuard(true);
    setTooltipAnchorIsHovered(false);
    setTimeout(() => {
      setTooltipTransitionGuard(false);
      if (!(tooltipIsHovered || tooltipAnchorIsHovered)) {
        updateAnchor(undefined);
      }
    }, 150);
  };

  const showTooltip = () => {
    const showIntervalTooltip = Boolean(focusIntervalInfo);

    const showStopOrStartEndOrIncidentTooltip = Boolean(focusStopBarInfo || focusStartEndInfo || focusIncidentInfo);

    const isTooltipHovered = tooltipIsHovered || tooltipAnchorIsHovered || tooltipTransitionGuard;

    return (showStopOrStartEndOrIncidentTooltip && isTooltipHovered) || showIntervalTooltip;
  };

  const adjustPosition = (barProps: any) => {
    const { x, y, contentHeight } = barProps;
    const adjustedStartX = x < 0 ? X_TICK_END_MARGIN : x;
    const neededXSpace = TOOLTIP_WIDTH + 20;
    const neededYSpace = TOOLTIP_EXT_HEIGHT + 20;

    const yBottomWithTooltip = y + neededYSpace;
    const openAbove = yBottomWithTooltip > chartHeight;

    const adjustedX = adjustedStartX - Math.max(adjustedStartX + neededXSpace - chartWidth, 0);
    const adjustedY = y - (openAbove ? contentHeight : 0);

    return { adjustedX, adjustedY };
  };

  const getTooltipPosition = () => {
    const info = focusStopBarInfo || focusStartEndInfo || focusIncidentInfo || focusIntervalInfo;

    if (info) {
      return {
        x: info.x,
        y: info.y + BAR_SIZE + 5,
      };
    }
  };

  const handleClickIncident = (vehicleId: UUID, time: number) => {
    Analytics.track('Tracking Hook On Incidents Popup Clicked In Tracking History');

    const params = queryString.stringify({
      vehicle_id: vehicleId,
      date: time,
      event_type: 'incident',
      event_time: time,
    });
    const url = `${window.location.origin}#${Location.TRACKING}?${params}`;
    window.open(url, '_blank')!.focus();
  };

  const CustomTooltip = (
    startEndInfo?: PositionedInfo<StartEndInfo>,
    stopBarInfo?: PositionedInfo<StopBarInfo>,
    incidentBarInfo?: PositionedInfo<IncidentBarInfo>,
    intervalInfo?: PositionedInfo<IntervalInfo>
  ) => {
    if (startEndInfo) {
      const { type, vehicleId, timestamp, driverName } = startEndInfo.info;
      const handleGoToTrackingTab = () => {
        Analytics.track('Opened Vehicle Tracking On Map From Tracking History Start End Popup', { source: type });

        const params = queryString.stringify({
          vehicle_id: vehicleId,
          date: timestamp,
        });
        const url = `${window.location.origin}#${Location.TRACKING}?${params}`;
        window.open(url, '_blank')!.focus();
      };

      return (
        <StartEndPopup
          times={{
            start: type === 'start' ? timestamp : undefined,
            end: type === 'end' ? timestamp : undefined,
          }}
          driverName={driverName}
        >
          <Button variant='ghost' startIcon={<Icon name='route' />} className='flex-1' onClick={handleGoToTrackingTab}>
            {t('see_on_map')}
          </Button>
        </StartEndPopup>
      );
    }

    if (stopBarInfo) {
      const { info } = stopBarInfo;

      return (
        <HistoryStopPopup stop={info.stop} vehicleId={info.vehicleId} idleTimeHighlighted={info.isIdle} filteredDuration={getHintInfoNotFilteredDurationInMillis(info.period)} />
      );
    }

    if (incidentBarInfo) {
      const { info } = incidentBarInfo;

      return (
        <IncidentPopup incidentOrCluster={info.incident} driverName={info.driverName}>
          <Link
            className='flex-1'
            to={{
              pathname: Location.TRIGGERS_CREATE,
              search: queryString.stringify({
                templateRule: RuleCategory.DEVICE_CONNECTION,
                templateCondition: ConditionCategory.DEVICE_CONNECTION_CONNECTED_DISCONNECTED,
              }),
            }}
          >
            <Button
              className='w-full'
              variant='outlined'
              size='sm'
              startIcon={<Icon name='plus' />}
              onClick={() => Analytics.track('Created Device Connection Rule by Hook Popup')}
            >
              {t('create_idle_engine_rule')}
            </Button>
          </Link>
          <Button variant='ghost' size='sm' className='w-full flex-1' startIcon={<Icon name='route' />} onClick={() => handleClickIncident(info.vehicleId, info.incident.time)}>
            {t('see_on_map')}
          </Button>
        </IncidentPopup>
      );
    }

    if (intervalInfo) {
      const notFilteredDuration = getHintInfoNotFilteredDurationInMillis(intervalInfo.info.period);
      return (
        <>
          {notFilteredDuration && (
            <div className={styles.hintWrapper}>
              <Typography variant='caption-default'>
                {t('you_are_viewing')} <b>{formatDuration(notFilteredDuration)}</b> {t('of_total_stop_time')}
              </Typography>
            </div>
          )}
        </>
      );
    }
  };

  // I am not proud of the need of this variable maxNumStops
  const maxNumStops = Math.max(...inputChartData.map(({ stops }) => (stops ? Object.keys(stops).length : 0)), 0);
  const maxNumIncidents = Math.max(...inputChartData.map(({ incidents }) => (incidents ? Object.keys(incidents).length : 0)), 0);

  const allTrackingIntervals = [...inputChartData.map(({ routeInterval }) => routeInterval)].filter(Boolean) as IntervalInfo[];
  const allTrackingStarts = allTrackingIntervals.map((interval) => interval.period[0]);
  const allTrackingEnds = allTrackingIntervals.map((interval) => interval.period[1]);

  const minTrackingStart = moment(allTrackingStarts.length ? Math.min(...allTrackingStarts) : period.start);
  const maxTrackingEnd = moment(allTrackingEnds.length ? Math.max(...allTrackingEnds) : period.end);
  const domainStart = Math.max(minTrackingStart.startOf('hour').valueOf(), period.start + wantedTimeSelection.start);
  const domainEnd = Math.min(maxTrackingEnd.endOf('hour').valueOf(), period.start + wantedTimeSelection.end);

  const numXTicks = Math.ceil((period.end - period.start) / ONE_HOUR_IN_MILLIS) + 1;
  const visibleXTicks = Math.ceil((domainEnd - domainStart) / ONE_HOUR_IN_MILLIS) + 1;
  const xTicks = Array(numXTicks)
    .fill(undefined)
    .map((_, idx) => period.start + idx * ONE_HOUR_IN_MILLIS);

  const chartData = inputChartData.concat(getExtraChartRows(domainStart));
  const spreadChartData = chartData.map(({ ...restEntry }) => ({
    ...restEntry.stops,
    ...restEntry.incidents,
    ...restEntry,
  }));

  const chartHeight = HEADER_SIZE + BAR_ROW_SIZE * chartData.length;
  const chartWidth = Math.max(X_TICK_WIDTH * xTicks.length + X_TICK_GAP_MARGIN * (xTicks.length - 1), MIN_CHART_WIDTH);

  return (
    <div className='w-full overflow-x-auto overflow-y-hidden'>
      <div className={styles.timelineWrapper}>
        <BarChart
          data={spreadChartData}
          width={chartWidth}
          height={chartHeight}
          margin={{ left: X_TICK_END_MARGIN, right: X_TICK_END_MARGIN }}
          layout='vertical'
          barSize={BAR_SIZE}
        >
          <CartesianGrid horizontal={false} style={styles.cartesianGrid} />

          {showTooltip() && (
            <Tooltip
              allowEscapeViewBox={{ x: true, y: true }}
              position={getTooltipPosition()}
              wrapperStyle={{ visibility: 'visible', width: '100%' }}
              cursor={{ fill: 'transparent' }}
              isAnimationActive={false}
              content={
                <div onMouseEnter={() => setTooltipIsHovered(true)} onMouseLeave={handleTooltipMouseLeave}>
                  {CustomTooltip(focusStartEndInfo, focusStopBarInfo, focusIncidentInfo, focusIntervalInfo)}
                </div>
              }
            />
          )}

          <YAxis dataKey='vehicleId' type='category' yAxisId={0} tickLine={false} axisLine={false} hide />
          <Bar
            isAnimationActive={false}
            dataKey='routeInterval.period'
            radius={3}
            yAxisId={0}
            label={CustomLabel}
            className={styles.routePeriod}
            onMouseEnter={handleIntervalMouseEnter}
            onMouseLeave={handleIntervalMouseLeave}
          />

          {/* Each stop bar must have its own axis to become a linear timeline */}
          {[...Array(maxNumStops)].map((_, idx) => (
            <YAxis dataKey='vehicleId' key={`stop-yaxis-${idx}`} type='category' yAxisId={idx + 1} tickLine={false} axisLine={false} hide />
          ))}
          {[...Array(maxNumStops)].map((_, idx) => (
            <Bar
              isAnimationActive={false}
              key={`stop${idx + 1}`}
              dataKey={`stop${idx + 1}.period`}
              yAxisId={idx + 1}
              shape={(barProps: any) => CustomStopBar(barProps.payload[`stop${idx + 1}`], barProps)}
              onMouseEnter={handleStopBarMouseEnter(idx)}
              onMouseLeave={handleTooltipAnchorMouseLeave(setFocusStopBarInfo)}
            />
          ))}

          {/* Each incident bar must have its own axis to become a linear timeline */}
          {[...Array(maxNumIncidents)].map((_, idx) => (
            <YAxis dataKey='vehicleId' key={`incident-yaxis-${idx}`} type='category' yAxisId={idx - maxNumIncidents} tickLine={false} axisLine={false} hide />
          ))}
          {[...Array(maxNumIncidents)].map((_, idx) => (
            <Bar
              isAnimationActive={false}
              key={`incident${idx + 1}`}
              dataKey={`incident${idx + 1}.position`}
              yAxisId={idx - maxNumIncidents}
              shape={(barProps: any) => CustomIncidentBar(barProps.payload[`incident${idx + 1}`], barProps)}
              onMouseEnter={handleIncidentBarMouseEnter(idx)}
              onMouseLeave={handleTooltipAnchorMouseLeave(setFocusIncidentInfo)}
            />
          ))}

          <XAxis
            ticks={xTicks}
            tickMargin={10}
            type='number'
            orientation='top'
            allowDataOverflow
            tickLine={false}
            axisLine={false}
            domain={[domainStart, domainEnd]}
            interval={0}
            tick={({ payload, x, y }: { payload: any; x: number; y: number }) => (
              <text x={x} y={y} className='text-black'>
                {moment(payload.value).format('k')}h {moment(payload.value).format('mm')}
              </text>
            )}
          />
        </BarChart>
      </div>
    </div>
  );
};
